import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';

const ROLE_LABELS = {
  platform_superuser:  'Platform Administrator',
  org_superadmin:      'Organization Administrator',
  compliance_officer:  'Compliance Officer',
  compliance_analyst:  'Compliance Analyst',
  department_lead:     'Department Lead',
  department_user:     'Team Member',
};

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invite, setInvite] = useState(null);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Missing invitation token.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/invite/${encodeURIComponent(token)}`);
        const data = await res.json();
        if (data.success) {
          setInvite(data.invite);
        } else {
          setError(data.message || 'Invitation is invalid or expired.');
        }
      } catch (err) {
        setError('Could not reach the server. Please try again.');
      }
      setLoading(false);
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (password.length < 8) return setSubmitError('Password must be at least 8 characters.');
    if (password !== confirm) return setSubmitError('Passwords do not match.');

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/auth/invite/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (data.success) {
        setDone(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setSubmitError(data.message || 'Could not activate your account.');
      }
    } catch (err) {
      setSubmitError('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  // ─── Loading state ───
  if (loading) {
    return (
      <Shell>
        <div className="text-center py-12">
          <div className="w-12 h-12 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400">Verifying your invitation…</p>
        </div>
      </Shell>
    );
  }

  // ─── Error state ───
  if (error) {
    return (
      <Shell>
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
            <span className="text-3xl">⚠</span>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Invitation problem</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
          >
            Go to sign in
          </button>
        </div>
      </Shell>
    );
  }

  // ─── Success state ───
  if (done) {
    return (
      <Shell>
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-emerald-500/20 rounded-full flex items-center justify-center">
            <span className="text-3xl">✓</span>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">You're all set</h2>
          <p className="text-slate-400 mb-6">Redirecting you to sign in…</p>
        </div>
      </Shell>
    );
  }

  // ─── Form ───
  return (
    <Shell>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-1">Welcome, {invite.name}</h2>
        <p className="text-sm text-slate-400">
          You've been invited to{' '}
          <span className="text-blue-400 font-medium">{invite.organization_name || 'Precision Compliance Group'}</span>
          {invite.user_type && (
            <> as a <span className="text-blue-400 font-medium">{ROLE_LABELS[invite.user_type] || 'Team Member'}</span></>
          )}
          .
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-slate-300 uppercase tracking-wide mb-1.5">Email</label>
          <input
            value={invite.email}
            disabled
            className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2.5 text-slate-400 cursor-not-allowed"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-300 uppercase tracking-wide mb-1.5">Set Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            autoFocus
            className="w-full bg-slate-800/80 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none transition"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-300 uppercase tracking-wide mb-1.5">Confirm Password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full bg-slate-800/80 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none transition"
          />
        </div>

        {submitError && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-4 py-3">
            {submitError}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !password || !confirm}
          className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-blue-500/25 transition-all"
        >
          {submitting ? 'Activating…' : 'Activate Account'}
        </button>
      </form>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[400px] opacity-[0.06]"
             style={{ background: 'radial-gradient(ellipse, #3b82f6 0%, transparent 70%)', filter: 'blur(80px)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[400px] opacity-[0.06]"
             style={{ background: 'radial-gradient(ellipse, #8b5cf6 0%, transparent 70%)', filter: 'blur(80px)' }} />
      </div>
      <div className="relative z-10 w-full max-w-md bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
            <span className="text-white font-extrabold text-[10px] tracking-[0.15em]">PCG</span>
          </div>
          <div>
            <h1 className="text-sm font-semibold text-white">Precision Compliance Group</h1>
            <p className="text-[11px] text-slate-500">Account Activation</p>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
